import React, { useState, useRef, useCallback, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { pronunciationService, PronunciationResult } from '../../services/pronunciationService';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import './ShadowingExperimentPage.css';

// ── Test data ───────────────────────────────────────────────
const AUDIO_BASE = '/uploads/audio/draft_0bf9ba18-a96a-4f47-bf6e-09b1d5a7ffc1_1773510854883';
const START_TIME = 0;
const END_TIME = Infinity;

// ── Visual annotation types for speaking strategy ────────────
type TextSegment = string | AnnotatedSegment;

interface AnnotatedSegment {
  text: string;
  type: 'stress' | 'link' | 'pause' | 'syllables' | 'reduction';
  hint?: string; // syllables: "i-MAG-in-able", reduction: "should-uv"
}

interface ChunkData {
  text: string;           // plain text (for Azure assessment)
  segments: TextSegment[]; // visual rendering segments
  intonation?: 'rising' | 'falling';
  tone?: string;          // emotional tone label
}

interface SentenceOption {
  id: string;
  file: string;
  text: string;
  speaker: string;
  chunks: ChunkData[];
}

const SENTENCES: SentenceOption[] = [
  {
    id: '001', file: `${AUDIO_BASE}/sentence_001.mp3`,
    text: "I've had the most dreadful morning imaginable.",
    speaker: 'Lisa',
    chunks: [
      {
        text: "I've had",
        segments: [{ text: "I've had", type: 'link' }],
      },
      {
        text: 'the most dreadful morning',
        segments: ['the most ', { text: 'dreadful', type: 'stress' }, ' ', { text: 'morning', type: 'stress' }],
      },
      {
        text: 'imaginable',
        segments: [{ text: 'imaginable', type: 'syllables', hint: 'i-MAG-in-able' }],
      },
    ],
  },
  {
    id: '002', file: `${AUDIO_BASE}/sentence_002.mp3`,
    text: 'Oh dear, you look rather shaken.',
    speaker: 'Olivia',
    chunks: [
      {
        text: 'Oh dear,',
        segments: ['Oh ', { text: 'dear,', type: 'pause' }],
      },
      {
        text: 'you look rather shaken',
        segments: ['you look ', { text: 'rather', type: 'stress' }, ' ', { text: 'shaken', type: 'stress' }],
      },
    ],
  },
  {
    id: '004', file: `${AUDIO_BASE}/sentence_004.mp3`,
    text: 'Someone stole my wallet on the underground this morning, and my passport was inside it.',
    speaker: 'Lisa',
    chunks: [
      {
        text: 'Someone stole my wallet',
        segments: ['Someone ', { text: 'stole', type: 'stress' }, ' my ', { text: 'wallet', type: 'stress' }],
      },
      {
        text: 'on the underground this morning,',
        segments: [{ text: 'on the', type: 'link' }, ' underground this morning,'],
      },
      {
        text: 'and my passport was inside it',
        segments: ['and my ', { text: 'passport', type: 'stress' }, ' was ', { text: 'inside', type: 'stress' }, ' it'],
      },
    ],
  },
  {
    id: '006', file: `${AUDIO_BASE}/sentence_006.mp3`,
    text: 'Had you not considered keeping your passport separate from your wallet?',
    speaker: 'Olivia',
    chunks: [
      {
        text: 'Had you not considered',
        segments: ['Had you not considered'],
        intonation: 'rising',
      },
      {
        text: 'keeping your passport separate',
        segments: ['keeping your passport ', { text: 'separate', type: 'syllables', hint: 'SEP-a-rate' }],
      },
      {
        text: 'from your wallet?',
        segments: ['from your wallet?'],
        intonation: 'falling',
      },
    ],
  },
  {
    id: '007', file: `${AUDIO_BASE}/sentence_007.mp3`,
    text: 'I know, I should have been more cautious.',
    speaker: 'Lisa',
    chunks: [
      {
        text: 'I know,',
        segments: ['I ', { text: 'know,', type: 'pause' }],
        tone: 'Regretful',
      },
      {
        text: 'I should have been',
        segments: ['I ', { text: 'should have', type: 'reduction', hint: 'should-uv' }, ' been'],
      },
      {
        text: 'more cautious',
        segments: ['more ', { text: 'cautious', type: 'stress' }],
      },
    ],
  },
  {
    id: '008', file: `${AUDIO_BASE}/sentence_008.mp3`,
    text: "I'm such a fool for carrying everything together.",
    speaker: 'Lisa',
    chunks: [
      {
        text: "I'm such a fool",
        segments: ["I'm such a ", { text: 'fool', type: 'stress' }],
        tone: 'Self-deprecating',
      },
      {
        text: 'for carrying everything together',
        segments: ['for carrying ', { text: 'everything', type: 'stress' }, ' ', { text: 'together', type: 'stress' }],
      },
    ],
  },
  {
    id: '010', file: `${AUDIO_BASE}/sentence_010.mp3`,
    text: 'These things happen even to the most seasoned travellers.',
    speaker: 'Olivia',
    chunks: [
      {
        text: 'These things happen',
        segments: ['These things happen'],
        tone: 'Reassuring',
      },
      {
        text: 'even to the most seasoned',
        segments: ['even to the most ', { text: 'seasoned', type: 'stress' }],
      },
      {
        text: 'travellers',
        segments: [{ text: 'travellers', type: 'syllables', hint: 'TRAV-el-lers' }],
      },
    ],
  },
];

// ── Render annotated chunk segments ─────────────────────────
function renderSegments(segments: TextSegment[]): React.ReactNode[] {
  return segments.map((seg, i) => {
    if (typeof seg === 'string') return <React.Fragment key={i}>{seg}</React.Fragment>;

    switch (seg.type) {
      case 'stress':
        return <span key={i} className="ann-stress">{seg.text}</span>;
      case 'link':
        return <span key={i} className="ann-link">{seg.text}</span>;
      case 'pause':
        return (
          <React.Fragment key={i}>
            <span className="ann-pause">{seg.text}</span>
            <span className="pause-mark">‖</span>
          </React.Fragment>
        );
      case 'syllables':
        return (
          <span key={i} className="ann-syllables">
            {seg.text}
            <span className="syllable-hint">{seg.hint}</span>
          </span>
        );
      case 'reduction':
        return (
          <span key={i} className="ann-reduction">
            {seg.text}
            <span className="reduction-hint">→ {seg.hint}</span>
          </span>
        );
      default:
        return <React.Fragment key={i}>{seg.text}</React.Fragment>;
    }
  });
}

function renderChunk(chunk: ChunkData, idx: number): React.ReactNode {
  return (
    <span key={idx} className={`chunk chunk-${idx % 3}`}>
      {chunk.tone && (
        <span className="ann-tone-wrapper">
          <span className="ann-tone">{chunk.tone}</span>
        </span>
      )}
      {renderSegments(chunk.segments)}
      {chunk.intonation === 'rising' && <span className="ann-intonation rising">↗</span>}
      {chunk.intonation === 'falling' && <span className="ann-intonation falling">↘</span>}
      {' '}
    </span>
  );
}

type Phase = 'pre-listen' | 'shadowing' | 'results';
type ShadowMode = 'scripted' | 'unscripted';

const SCORE_WEIGHTS = { accuracy: 0.4, fluency: 0.3, completeness: 0.3 };
const UNLOCK_SCORE = 70; // Minimum score to unlock unscripted mode

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#eab308';
  return '#ef4444';
};

const getWordClass = (score: number, errorType: string): string => {
  if (errorType === 'Omission') return 'omission';
  if (score >= 80) return 'good';
  if (score >= 60) return 'ok';
  return 'bad';
};

export default function ShadowingExperimentPage() {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [phase, setPhase] = useState<Phase>('pre-listen');
  const [shadowMode, setShadowMode] = useState<ShadowMode>('scripted');
  const [hasListened, setHasListened] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAssessing, setIsAssessing] = useState(false);
  const [result, setResult] = useState<PronunciationResult | null>(null);
  const [scriptedResult, setScriptedResult] = useState<PronunciationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // AudioWorklet-based recording via hook (AEC disabled for headphone use)
  const {
    isRecording,
    audioBlob: recordingBlob,
    audioUrl: recordingUrl,
    startRecording: hookStartRecording,
    stopRecording: hookStopRecording,
    clearRecording,
    error: recError,
    analyserNode,
  } = useAudioRecorder({
    audioConstraints: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
  });

  // Refs
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const wavesurferContainerRef = useRef<HTMLDivElement | null>(null);
  const micCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const animFrameRef = useRef<number>(0);
  const audioFinishedRef = useRef(false);
  const silenceStartRef = useRef<number>(0);
  const playbackStartedRef = useRef(false);
  const maxRecordingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAssessedRef = useRef(false);
  const shadowingReadyRef = useRef(false);

  // Propagate recording errors
  useEffect(() => {
    if (recError) setError(recError);
  }, [recError]);

  const sentence = SENTENCES[selectedIdx];
  const audioUrl = resolveMediaUrl(sentence.file);

  // ── Phase 1: Pre-listen ─────────────────────────────────
  const handlePreListen = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = START_TIME;
    audio.play();
    setIsPlaying(true);
  }, []);

  // Monitor audio timeupdate for segment boundary
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      if (END_TIME !== Infinity && audio.currentTime >= END_TIME) {
        audio.pause();
        setIsPlaying(false);
        setHasListened(true);
      }
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    const onEnded = () => { setIsPlaying(false); setHasListened(true); };
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  // ── Silence / recording stop ──────────────────────────────
  const SILENCE_THRESHOLD = 0.01;
  const SILENCE_DURATION_MS = 1000;

  const stopRecording = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    if (maxRecordingTimerRef.current) {
      clearTimeout(maxRecordingTimerRef.current);
      maxRecordingTimerRef.current = null;
    }

    if (wavesurferRef.current) {
      wavesurferRef.current.pause();
    }

    // Delegate to hook — encodes PCM → WAV, sets audioBlob/audioUrl
    hookStopRecording();

    setPhase('results');
  }, [hookStopRecording]);

  // ── Mic waveform + silence detection ──────────────────────
  const drawMicWaveform = useCallback(() => {
    const canvas = micCanvasRef.current;
    const analyser = analyserNode;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      // Calculate RMS
      let sumSquares = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = (dataArray[i] - 128) / 128.0;
        sumSquares += v * v;
      }
      const rms = Math.sqrt(sumSquares / bufferLength);

      // Track playback state
      if (!playbackStartedRef.current && wavesurferRef.current && wavesurferRef.current.isPlaying()) {
        playbackStartedRef.current = true;
      }
      if (!audioFinishedRef.current && playbackStartedRef.current && wavesurferRef.current && !wavesurferRef.current.isPlaying()) {
        audioFinishedRef.current = true;
      }

      // Silence detection after audio finishes
      if (audioFinishedRef.current) {
        if (rms < SILENCE_THRESHOLD) {
          if (silenceStartRef.current === 0) {
            silenceStartRef.current = performance.now();
          } else if (performance.now() - silenceStartRef.current >= SILENCE_DURATION_MS) {
            stopRecording();
            return;
          }
        } else {
          silenceStartRef.current = 0;
        }
      }

      // Draw waveform — amplified for visibility
      ctx.fillStyle = '#052e16';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Glow effect
      ctx.shadowColor = '#4ade80';
      ctx.shadowBlur = 6;
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = '#4ade80';
      ctx.beginPath();

      const amplify = 2.5; // boost signal for visual clarity
      const sliceWidth = canvas.width / bufferLength;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const raw = (dataArray[i] - 128) / 128.0;
        const amplified = Math.max(-1, Math.min(1, raw * amplify));
        const v = (amplified + 1) / 2;
        const y = v * canvas.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += sliceWidth;
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Draw a second thinner bright line on top for crispness
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#86efac';
      ctx.beginPath();
      x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const raw = (dataArray[i] - 128) / 128.0;
        const amplified = Math.max(-1, Math.min(1, raw * amplify));
        const v = (amplified + 1) / 2;
        const y = v * canvas.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += sliceWidth;
      }
      ctx.stroke();
    };

    draw();
  }, [analyserNode, stopRecording]);

  // ── Init WaveSurfer ───────────────────────────────────────
  const initWaveSurfer = useCallback(() => {
    if (!wavesurferContainerRef.current) return;

    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
    }

    const ws = WaveSurfer.create({
      container: wavesurferContainerRef.current,
      waveColor: '#667eea',
      progressColor: '#4f46e5',
      cursorColor: '#fff',
      cursorWidth: 2,
      height: 80,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
      backend: 'WebAudio',
    });

    ws.load(audioUrl);
    wavesurferRef.current = ws;

    ws.on('ready', () => {
      if (START_TIME > 0) {
        const duration = ws.getDuration();
        ws.seekTo(START_TIME / duration);
      }
    });

    ws.on('timeupdate', (currentTime: number) => {
      if (END_TIME !== Infinity && currentTime >= END_TIME) {
        ws.pause();
        audioFinishedRef.current = true;
      }
    });

    ws.on('finish', () => {
      audioFinishedRef.current = true;
    });

    return ws;
  }, [audioUrl]);

  // ── Start shadowing (via useAudioRecorder hook) ──────────────────
  const startShadowing = useCallback(async (mode: ShadowMode) => {
    setError(null);
    setResult(null);
    clearRecording();
    audioFinishedRef.current = false;
    silenceStartRef.current = 0;
    playbackStartedRef.current = false;
    hasAssessedRef.current = false;

    try {
      // Hook handles getUserMedia, AudioContext, AudioWorklet setup
      await hookStartRecording();

      setShadowMode(mode);
      shadowingReadyRef.current = true;
      setPhase('shadowing');
    } catch (err) {
      console.error('Failed to start shadowing:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to start shadowing.');
      }
    }
  }, [hookStartRecording, clearRecording]);

  // ── Phase 2 effect: init WaveSurfer + start playback ──────
  useEffect(() => {
    if (phase !== 'shadowing' || !shadowingReadyRef.current) return;
    shadowingReadyRef.current = false;

    const ws = initWaveSurfer();
    if (!ws) return;

    // Set canvas size for mic waveform
    const canvas = micCanvasRef.current;
    if (canvas) {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    }
    drawMicWaveform();

    // Start audio playback once ready
    const startPlayback = () => {
      if (START_TIME > 0) {
        const duration = ws.getDuration();
        ws.seekTo(START_TIME / duration);
      }
      ws.play();
    };

    if (ws.getDuration() > 0) {
      startPlayback();
    } else {
      ws.on('ready', startPlayback);
    }

    // Max recording safety (audio ~3-6s + buffer)
    maxRecordingTimerRef.current = setTimeout(() => {
      stopRecording();
    }, 15000);
  }, [phase, initWaveSurfer, drawMicWaveform, stopRecording]);

  // ── Phase 3: Assessment ───────────────────────────────────
  useEffect(() => {
    if (phase !== 'results' || !recordingBlob || hasAssessedRef.current) return;
    hasAssessedRef.current = true;

    const assess = async () => {
      setIsAssessing(true);
      try {
        // WAV blob — send directly via FormData
        const res = await pronunciationService.assessPronunciationWithFile(recordingBlob, sentence.text);
        setResult(res);
        // Save scripted result for unlock check
        if (shadowMode === 'scripted') {
          setScriptedResult(res);
        }
      } catch (err) {
        console.error('Assessment failed:', err);
        setError(err instanceof Error ? err.message : 'Assessment failed');
      } finally {
        setIsAssessing(false);
      }
    };

    assess();
  }, [phase, recordingBlob, sentence.text, shadowMode]);

  // ── Computed: scripted score for unlock ────────────────────
  const scriptedOverall = scriptedResult
    ? Math.round(
        scriptedResult.accuracyScore * SCORE_WEIGHTS.accuracy +
        scriptedResult.fluencyScore * SCORE_WEIGHTS.fluency +
        scriptedResult.completenessScore * SCORE_WEIGHTS.completeness
      )
    : 0;
  const unscriptedUnlocked = scriptedOverall >= UNLOCK_SCORE;

  // ── Reset ─────────────────────────────────────────────────
  const handleReset = useCallback((toMode?: ShadowMode) => {
    clearRecording();
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }
    cancelAnimationFrame(animFrameRef.current);

    setPhase('pre-listen');
    setHasListened(false);
    setIsPlaying(false);
    setIsAssessing(false);
    setResult(null);
    setError(null);
    audioFinishedRef.current = false;
    silenceStartRef.current = 0;
    hasAssessedRef.current = false;
    playbackStartedRef.current = false;
    shadowingReadyRef.current = false;
    if (maxRecordingTimerRef.current) {
      clearTimeout(maxRecordingTimerRef.current);
      maxRecordingTimerRef.current = null;
    }

    if (toMode) {
      setShadowMode(toMode);
    }
  }, [clearRecording]);

  const handleFullReset = useCallback(() => {
    setScriptedResult(null);
    setShadowMode('scripted');
    handleReset();
  }, [handleReset]);

  // Cleanup on unmount (WaveSurfer + animation frame only; stream/AudioContext handled by hook)
  useEffect(() => {
    return () => {
      if (wavesurferRef.current) wavesurferRef.current.destroy();
      cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // ── Render ────────────────────────────────────────────────
  const overallScore = result
    ? Math.round(
        result.accuracyScore * SCORE_WEIGHTS.accuracy +
        result.fluencyScore * SCORE_WEIGHTS.fluency +
        result.completenessScore * SCORE_WEIGHTS.completeness
      )
    : 0;

  // Determine which phases are completed
  const isScriptedDone = scriptedResult !== null;
  const showTextInShadowing = shadowMode === 'scripted';

  return (
    <div className="shadowing-experiment">
      <h1>Shadowing Experiment</h1>
      <p className="subtitle">Progressive shadowing: listen, scripted, then unscripted</p>

      {/* Sentence selector */}
      {phase === 'pre-listen' && (
        <div className="sentence-selector">
          <label>Select Sentence</label>
          <select
            value={selectedIdx}
            onChange={(e) => { setSelectedIdx(Number(e.target.value)); setHasListened(false); setScriptedResult(null); }}
          >
            {SENTENCES.map((s, i) => (
              <option key={s.id} value={i}>
                #{s.id} {s.speaker}: {s.text.slice(0, 50)}{s.text.length > 50 ? '...' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Phase indicator — 3 stages */}
      <div className="phase-indicator">
        <div className={`phase-step ${phase === 'pre-listen' ? 'active' : 'completed'}`}>
          1. Pre-listen
        </div>
        <div className={`phase-step ${
          phase === 'shadowing' && shadowMode === 'scripted' ? 'active' :
          phase === 'results' && shadowMode === 'scripted' ? 'active' :
          isScriptedDone ? 'completed' : ''
        }`}>
          2. Scripted
        </div>
        <div className={`phase-step ${
          phase === 'shadowing' && shadowMode === 'unscripted' ? 'active' :
          phase === 'results' && shadowMode === 'unscripted' ? 'active' : ''
        } ${!unscriptedUnlocked && !isScriptedDone ? 'locked' : ''}`}>
          3. Unscripted
          {!unscriptedUnlocked && isScriptedDone && (
            <span className="lock-icon" title={`Score ${UNLOCK_SCORE}+ to unlock`}> 🔒</span>
          )}
        </div>
      </div>

      {/* Sentence card — annotations visible in pre-listen & scripted, blurred in unscripted */}
      <div className={`sentence-card ${
        (phase === 'shadowing' || phase === 'results') && shadowMode === 'unscripted' ? 'text-hidden' : ''
      }`}>
        <div className="speaker">{sentence.speaker}</div>
        <div className="sentence-text">
          {sentence.chunks.map((chunk, i) => renderChunk(chunk, i))}
        </div>
        {(phase === 'shadowing' || phase === 'results') && shadowMode === 'unscripted' && (
          <div className="text-hidden-overlay">
            <span>Text hidden — shadow by ear</span>
          </div>
        )}
        {/* Annotation legend */}
        {phase === 'pre-listen' && (
          <div className="annotation-legend">
            <span className="legend-item"><span className="ann-stress">Bold</span> = stress</span>
            <span className="legend-item"><span className="ann-link" style={{ fontSize: '0.75rem' }}>arc</span> = link</span>
            <span className="legend-item"><span className="pause-mark" style={{ margin: 0 }}>‖</span> = pause</span>
            <span className="legend-item"><span className="ann-intonation rising" style={{ fontSize: '0.85rem' }}>↗</span><span className="ann-intonation falling" style={{ fontSize: '0.85rem' }}>↘</span> = intonation</span>
          </div>
        )}
      </div>

      {/* Hidden audio element for Phase 1 */}
      <audio ref={audioRef} src={audioUrl} preload="auto" />

      {/* Error display */}
      {error && <div className="error-box">{error}</div>}

      {/* ── Phase 1: Pre-listen ── */}
      {phase === 'pre-listen' && (
        <div>
          <div className="headphone-banner">
            <span className="icon">🎧</span>
            <span>Use headphones for the best experience — prevents audio feedback during recording.</span>
          </div>

          <div className="button-row">
            <button
              className="action-btn btn-primary"
              onClick={handlePreListen}
              disabled={isPlaying}
            >
              {isPlaying ? 'Playing...' : hasListened ? 'Listen Again' : 'Listen to Sentence'}
            </button>
          </div>

          {hasListened && (
            <div className="button-row">
              <button
                className="action-btn btn-record"
                onClick={() => startShadowing(shadowMode)}
              >
                {shadowMode === 'unscripted'
                  ? 'Start Unscripted Shadowing'
                  : 'Start Scripted Shadowing'}
              </button>
            </div>
          )}

          {/* Mode info */}
          {hasListened && (
            <div className="mode-info">
              {shadowMode === 'scripted' ? (
                <p>📝 <strong>Scripted mode</strong> — text will be visible while you shadow. Focus on pronunciation and rhythm.</p>
              ) : (
                <p>👂 <strong>Unscripted mode</strong> — text will be hidden. Shadow purely by listening.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Phase 2: Shadowing ── */}
      {phase === 'shadowing' && (
        <div>
          <div className="waveform-label">
            <span className="dot system" />
            System Audio
          </div>
          <div className="system-waveform" ref={wavesurferContainerRef} />

          <div className="waveform-section">
            <div className="waveform-label">
              <span className="dot mic" />
              Your Voice
              {isRecording && (
                <span className="rec-badge">
                  <span className="rec-dot" />
                  {showTextInShadowing ? 'Speak along' : 'Shadow by ear'}
                </span>
              )}
            </div>
            <div className="mic-waveform">
              <canvas ref={micCanvasRef} />
            </div>
          </div>

          <div className="button-row">
            <button className="action-btn btn-secondary" onClick={stopRecording}>
              Stop Early
            </button>
          </div>
        </div>
      )}

      {/* ── Phase 3: Results ── */}
      {phase === 'results' && (
        <div className="results-section">
          {isAssessing && (
            <div className="assessing-card">
              <div className="spinner" />
              <span>Analyzing pronunciation...</span>
            </div>
          )}

          {result && (
            <div className="results-card">
              {/* Mode badge */}
              <div className="mode-badge-row">
                <span className={`mode-badge ${shadowMode}`}>
                  {shadowMode === 'scripted' ? '📝 Scripted' : '👂 Unscripted'}
                </span>
              </div>
              {/* Score header: circle + bars side by side */}
              <div className="score-header">
                <div className="overall-score">
                  <div
                    className="score-circle"
                    style={{ borderColor: getScoreColor(overallScore) }}
                  >
                    <span className="score-value" style={{ color: getScoreColor(overallScore) }}>
                      {overallScore}
                    </span>
                    <span className="score-label">Overall</span>
                  </div>
                </div>

                <div className="score-bars">
                  {[
                    { label: `Accuracy (${SCORE_WEIGHTS.accuracy * 100}%)`, value: result.accuracyScore },
                    { label: `Fluency (${SCORE_WEIGHTS.fluency * 100}%)`, value: result.fluencyScore },
                    { label: `Completeness (${SCORE_WEIGHTS.completeness * 100}%)`, value: result.completenessScore },
                  ].map((item) => (
                    <div key={item.label} className="score-bar-item">
                      <span className="bar-label">{item.label}</span>
                      <div className="bar-track">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${item.value}%`,
                            background: getScoreColor(item.value),
                          }}
                        />
                      </div>
                      <span className="bar-value" style={{ color: getScoreColor(item.value) }}>
                        {Math.round(item.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comparison with scripted score (inline in card) */}
              {shadowMode === 'unscripted' && scriptedResult && (
                <div className="score-comparison">
                  <h3>Scripted vs Unscripted</h3>
                  <div className="comparison-row">
                    <div className="comparison-item">
                      <span className="comparison-label">Scripted</span>
                      <span className="comparison-score" style={{ color: getScoreColor(scriptedOverall) }}>
                        {scriptedOverall}
                      </span>
                    </div>
                    <span className="comparison-arrow">→</span>
                    <div className="comparison-item">
                      <span className="comparison-label">Unscripted</span>
                      <span className="comparison-score" style={{ color: getScoreColor(overallScore) }}>
                        {overallScore}
                      </span>
                    </div>
                    <span className={`comparison-delta ${overallScore >= scriptedOverall ? 'positive' : 'negative'}`}>
                      {overallScore >= scriptedOverall ? '+' : ''}{overallScore - scriptedOverall}
                    </span>
                  </div>
                </div>
              )}

              {/* Word analysis */}
              <div className="word-analysis">
                <h3>Word-by-Word Analysis</h3>
                <div className="word-chips">
                  {result.words
                    .filter(w => w.errorType !== 'Insertion')
                    .map((w, i) => (
                      <span
                        key={i}
                        className={`word-chip ${getWordClass(w.accuracyScore, w.errorType)}`}
                        title={`${w.errorType !== 'None' ? w.errorType + ' — ' : ''}Score: ${Math.round(w.accuracyScore)}`}
                      >
                        {w.word}
                      </span>
                    ))}
                </div>
              </div>

              {/* Recognized text */}
              {result.recognizedText && (
                <div className="recognized-text">
                  <strong>Recognized:</strong> {result.recognizedText}
                </div>
              )}
            </div>
          )}

          {/* Playback */}
          {recordingUrl && (
            <div className="playback-section">
              <span className="playback-label">Your Recording</span>
              <audio controls src={recordingUrl} />
            </div>
          )}

          {/* Actions */}
          <div className="button-row">
            {shadowMode === 'scripted' && result && unscriptedUnlocked ? (
              <button
                className="action-btn btn-primary"
                onClick={() => handleReset('unscripted')}
              >
                Advance to Unscripted →
              </button>
            ) : (
              <button
                className="action-btn btn-primary"
                onClick={() => handleReset(shadowMode)}
              >
                Try Again
              </button>
            )}
          </div>

          {/* Secondary actions */}
          {shadowMode === 'scripted' && result && !unscriptedUnlocked && (
            <div className="unlock-hint">
              Score {UNLOCK_SCORE}+ to unlock Unscripted mode (current: {overallScore})
            </div>
          )}

          {shadowMode === 'scripted' && result && unscriptedUnlocked && (
            <div className="button-row">
              <button
                className="action-btn btn-secondary"
                onClick={() => handleReset(shadowMode)}
              >
                Try Again (Scripted)
              </button>
            </div>
          )}

          <div className="button-row">
            <button className="action-btn btn-text" onClick={handleFullReset}>
              Start Over (New Sentence)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
